import { useEffect, useState } from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, Icon, Text } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';

import { FirmBillableAccountsCsvExportBanner } from './FirmBillableAccountsCsvExportBanner';

import { CsvExportStatus } from '../../../../graphql/schema';
import { CreateFirmBillableAccountsCsvExportDocument } from '../graphql/mutation.createFirmBillableAccountsCsvExport.generated';
import {
  FirmBillableAccountsCsvExportDocument,
  FirmBillableAccountsCsvExportQuery,
} from '../graphql/query.firmBillableAccountsCsvExport.generated';

const STOP_POLLING_STATUSES = [CsvExportStatus.Complete, CsvExportStatus.Error];

export const DownloadCsvTemplate = () => {
  const [createFirmBillableAccountsCsvExport] = useGraphQLMutation(CreateFirmBillableAccountsCsvExportDocument);
  const {
    data: queryData,
    startPolling,
    stopPolling,
  } = useGraphQLQuery<FirmBillableAccountsCsvExportQuery>(FirmBillableAccountsCsvExportDocument, {
    fetchPolicy: 'cache-and-network',
  });

  const [csvExportData, setCsvExportData] = useState(queryData?.firmBillableAccountsCsvExport);

  const hasCsvExportData = () => csvExportData != null;

  useEffect(() => {
    if (queryData?.firmBillableAccountsCsvExport != null) {
      setCsvExportData(queryData.firmBillableAccountsCsvExport);
    }
  }, [queryData]);

  useEffect(() => {
    if (csvExportData != null) {
      const { status } = csvExportData;

      if (status === CsvExportStatus.Generating) {
        startPolling(15000);
      } else if (STOP_POLLING_STATUSES.includes(status)) {
        stopPolling();
      }
    }
  }, [csvExportData, startPolling, stopPolling]);

  const handleDownloadCsvTemplate = () => {
    if (hasCsvExportData()) {
      return;
    }

    void (async () => {
      const result = await createFirmBillableAccountsCsvExport();
      if (result.data?.createFirmBillableAccountsCsvExport.result) {
        const newExport = result.data.createFirmBillableAccountsCsvExport.result;
        setCsvExportData(newExport);
      }
    })();
  };

  const handleBannerDismiss = () => {
    setCsvExportData(null);
  };

  return (
    <Box
      css={{
        p: 'space.300',
        borderColor: 'border.weak',
        borderStyle: 'solid',
        borderWidth: 'thin',
        borderRadius: 'medium',
        width: '100%',
      }}
    >
      <Box css={{ pb: 'space.200' }}>
        <ol
          style={{
            paddingLeft: token('spacing.space.200'),
            marginTop: token('spacing.space.0'),
            marginBottom: token('spacing.space.0'),
          }}
        >
          <li>Download the on-demand billing CSV template.</li>
          <li>
            Enter the fee amount and an optional description (clients will see this) in columns F and G for the accounts
            you want to bill.
          </li>
          <li>Delete rows for accounts you won't bill.</li>
          <li>Save and upload the CSV.</li>
        </ol>
      </Box>
      <Text variant="text50" color="text.weak" css={{ pt: 'space.200' }}>
        Fees will not be assessed from HSA or standard 401(k) accounts.
      </Text>
      <Box
        css={{
          backgroundColor: 'background.surface.hover',
          px: 'space.300',
          py: 'space.200',
          mt: 'space.300',
          borderRadius: 'medium',
        }}
      >
        <Button onClick={handleDownloadCsvTemplate} variant="textPrimary">
          <Icon
            name="Download"
            size="small"
            css={{ verticalAlign: 'middle', display: 'inline-block', marginBottom: 'space.50' }}
          />
          On-demand billing CSV template
        </Button>
      </Box>
      {hasCsvExportData() && (
        <Box css={{ pt: 'space.200' }}>
          <FirmBillableAccountsCsvExportBanner
            firmBillableAccountsCsvExportStatus={csvExportData!.status}
            onDismiss={handleBannerDismiss}
          />
        </Box>
      )}
    </Box>
  );
};
